/**
 * Visual Search — camera-based insect identification in the search flydown.
 * Web Component: <visual-search data-api-url="…" data-api-key="…">
 */
(function () {
  'use strict';

  /* ── helpers ── */
  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function resizeImage(file, maxW, quality, cb) {
    var reader = new FileReader();
    reader.onload = function (e) {
      var origUrl = e.target.result;
      var img = new Image();
      img.onload = function () {
        var w = img.width, h = img.height;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        cb(c.toDataURL('image/jpeg', quality), origUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  /* ── rate limiter (100 / hour, localStorage) ── */
  var RL_KEY = 'sk_visual_search_uploads';
  function getRateData() {
    try {
      var d = JSON.parse(localStorage.getItem(RL_KEY));
      if (d && typeof d.ts === 'number' && Array.isArray(d.times)) return d;
    } catch (_) { /* ignore */ }
    return { ts: 0, times: [] };
  }
  function checkRate() {
    var d = getRateData();
    var now = Date.now();
    d.times = d.times.filter(function (t) { return now - t < 3600000; });
    localStorage.setItem(RL_KEY, JSON.stringify(d));
    return d.times.length < 100;
  }
  function recordRate() {
    var d = getRateData();
    d.times.push(Date.now());
    localStorage.setItem(RL_KEY, JSON.stringify(d));
  }

  /* ── Web Component ── */
  class VisualSearch extends HTMLElement {
    connectedCallback() {
      this._apiUrl = this.getAttribute('data-api-url') || '';
      this._apiKey = this.getAttribute('data-api-key') || '';
      this._abortCtrl = null;
      this._cardData = null;     // latest AI card HTML
      this._thumbUrl = null;     // uploaded image thumbnail
      this._observer = null;

      this._fileInput = this.querySelector('[data-vs-file]');
      this._btn = this.querySelector('[data-vs-btn]');
      if (!this._fileInput || !this._btn) return;

      var self = this;
      this._btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self._fileInput.click();
      });

      this._fileInput.addEventListener('change', function () {
        if (this.files && this.files[0]) self._handleFile(this.files[0]);
        this.value = '';
      });

      // Open flydown on identification to show results
      this._searchEl = this.closest('[data-live-search]');
      this._resultsEl = this._searchEl
        ? this._searchEl.querySelector('[data-live-search-results]')
        : null;
      this._flydown = this._searchEl
        ? this._searchEl.querySelector('[data-live-search-flydown]')
        : null;
      this._input = this._searchEl
        ? this._searchEl.querySelector('[data-live-search-input]')
        : null;

      // MutationObserver: re-inject card if LiveSearch replaces innerHTML
      if (this._resultsEl) {
        this._observer = new MutationObserver(function () {
          if (self._cardData && !self._resultsEl.querySelector('.vs-card')) {
            self._injectCard();
          }
        });
        this._observer.observe(this._resultsEl, { childList: true });
      }
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._abortCtrl) this._abortCtrl.abort();
    }

    _openFlydown() {
      if (this._searchEl) {
        this._searchEl.classList.add('live-search--focused');
        this._searchEl.classList.add('live-search--has-results');
      }
      // Empire theme controls flydown via data-animation-state attribute + height
      if (this._flydown) {
        this._flydown.setAttribute('data-animation', 'closed=>open');
        this._flydown.setAttribute('data-animation-state', 'open');
      }
      // Ensure results container is visible (Empire theme sets opacity:0 by default)
      if (this._resultsEl) {
        this._resultsEl.style.display = 'block';
        this._resultsEl.style.opacity = '1';
      }
    }

    _updateFlydownHeight() {
      // Set flydown height to match its content (theme uses --open-height CSS var)
      if (this._flydown) {
        var h = this._flydown.scrollHeight;
        if (h < 50) h = 200; // minimum fallback
        this._flydown.style.setProperty('--open-height', h + 'px');
        this._flydown.style.height = h + 'px';
      }
    }

    _handleFile(file) {
      var validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (validTypes.indexOf(file.type) === -1) {
        this._showError('Nur JPEG, PNG oder WebP Dateien erlaubt.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this._showError('Datei zu groß. Maximum: 10 MB.');
        return;
      }
      if (!checkRate()) {
        this._showError('Limit erreicht. Bitte versuche es in einer Stunde erneut.');
        return;
      }

      var self = this;
      this._openFlydown();
      this._showLoading();

      resizeImage(file, 1600, 0.8, function (dataUrl, origUrl) {
        self._thumbUrl = origUrl;
        self._callApi(dataUrl);
      });
    }

    _callApi(base64) {
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();
      var self = this;

      fetch(this._apiUrl + '/insect/identify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this._apiKey
        },
        body: JSON.stringify({ image: base64, consent_commercial_use: false }),
        signal: this._abortCtrl.signal
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (e) { throw new Error(e.detail || 'Fehler ' + r.status); })
              .catch(function (e) { if (e.message) throw e; throw new Error('Fehler ' + r.status); });
          }
          return r.json();
        })
        .then(function (data) {
          recordRate();
          self._removeLoading();
          self._buildCard(data);
          self._injectCard();
          // Fill search input with German species name → triggers LiveSearch
          if (data.identified && data.species_name_de && self._input) {
            self._input.value = data.species_name_de;
            self._input.dispatchEvent(new Event('keyup', { bubbles: true }));
            self._input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          self._removeLoading();
          self._showError(err.message || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
        });
    }

    _showLoading() {
      // Remove previous card/error
      this._cardData = null;
      if (this._resultsEl) {
        var prev = this._resultsEl.querySelector('.vs-card, .vs-error');
        if (prev) prev.remove();
      }
      // Insert spinner into the results area (not flydown) so it's visible
      if (this._resultsEl) {
        var existing = this._resultsEl.querySelector('.vs-loading');
        if (existing) existing.remove();
        var el = document.createElement('div');
        el.className = 'vs-loading';
        el.innerHTML =
          '<div class="vs-loading__inner">' +
            '<svg class="vs-loading__spinner" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
              '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.4 31.4" stroke-linecap="round">' +
                '<animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>' +
              '</circle>' +
            '</svg>' +
            '<span>Bild wird analysiert…</span>' +
          '</div>';
        this._resultsEl.prepend(el);
        this._updateFlydownHeight();
      }
    }

    _removeLoading() {
      if (this._resultsEl) {
        var l = this._resultsEl.querySelector('.vs-loading');
        if (l) l.remove();
      }
    }

    _showError(msg) {
      this._openFlydown();
      this._removeLoading();
      if (!this._resultsEl) return;
      var prev = this._resultsEl.querySelector('.vs-error');
      if (prev) prev.remove();
      var el = document.createElement('div');
      el.className = 'vs-error';
      el.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>' +
        '<span>' + esc(msg) + '</span>';
      this._resultsEl.prepend(el);
      this._updateFlydownHeight();
    }

    _buildCard(data) {
      var html = '<div class="vs-card">';

      if (!data.identified) {
        html += '<div class="vs-card__body vs-card__body--not-found">';
        html += '<svg class="vs-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
        html += '<div class="vs-card__text">';
        html += '<p class="vs-card__title">Nicht erkannt</p>';
        html += '<p class="vs-card__desc">Das Bild konnte nicht identifiziert werden. Versuche es mit einem deutlicheren Foto.</p>';
        html += '</div></div>';
        html += '<div class="vs-card__fallback">';
        html += '<p class="vs-card__fallback-hint">Vielleicht hilft dir das weiter:</p>';
        html += '<div class="vs-card__fallback-links">';
        html += '<a class="vs-card__fallback-link" href="/products/aktiv-mikrodunger-konzentrat-1000-ml">';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="16" height="16"><path d="M12 22c4.97 0 9-2.69 9-6v-4M12 22c-4.97 0-9-2.69-9-6v-4M12 22c2.76 0 5-2.69 5-6v-4M12 22c-2.76 0-5-2.69-5-6v-4M3 12c0-3.31 4.03-6 9-6s9 2.69 9 6M7 12V8M17 12V8M12 12V2"/></svg>';
        html += ' Universaldünger';
        html += '</a>';
        html += '<a class="vs-card__fallback-link" href="/collections/dunger">';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="16" height="16"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>';
        html += ' Alle Dünger';
        html += '</a>';
        html += '</div></div>';
        html += '</div>';
        this._cardData = html;
        return;
      }

      // Thumbnail
      html += '<div class="vs-card__body">';
      if (this._thumbUrl) {
        html += '<img class="vs-card__thumb" src="' + this._thumbUrl + '" alt="" />';
      }
      html += '<div class="vs-card__text">';

      // Header
      html += '<p class="vs-card__label">Erkennung</p>';
      html += '<p class="vs-card__title">' + esc(data.species_name_de || 'Unbekannt') + '</p>';

      // Scientific + confidence
      var meta = [];
      if (data.scientific_name) meta.push('<em>' + esc(data.scientific_name) + '</em>');
      if (data.confidence != null) {
        var pct = Math.round(data.confidence * 100);
        meta.push(pct + '% sicher');
      }
      if (meta.length) html += '<p class="vs-card__meta">' + meta.join(' · ') + '</p>';

      // Description (max 2 lines)
      if (data.description) {
        var desc = data.description;
        if (desc.length > 160) desc = desc.substring(0, 157) + '…';
        html += '<p class="vs-card__desc">' + esc(desc) + '</p>';
      }

      // Glossary link
      if (data.glossary_match && data.glossary_match.url) {
        html += '<a class="vs-card__link" href="' + esc(data.glossary_match.url) + '">Mehr erfahren →</a>';
      }

      html += '</div></div></div>';
      this._cardData = html;
    }

    _injectCard() {
      if (!this._resultsEl || !this._cardData) return;
      // Remove old card first
      var old = this._resultsEl.querySelector('.vs-card');
      if (old) old.remove();
      this._resultsEl.insertAdjacentHTML('afterbegin', this._cardData);
      this._updateFlydownHeight();
    }
  }

  customElements.define('visual-search', VisualSearch);
})();
